import { unreachableCaseError } from "@edgetier/utilities";
import type { IFilter } from "./filters.types";

/**
 * Function to get the filter name based on the filter type.
 * @param {object} filter - The filter configuration.
 * @returns {string} The filter name.
 */
export const getFilterName = (filter: IFilter): string => {
    switch (filter.type) {
        case "date-range":
            return filter.startDateName;
        case "select":
        case "search":
        case "select-enum":
        case "custom":
            return filter.name;
        default:
            unreachableCaseError(filter);
            return "unknown";
    }
};

/**
 * Function to get the filter components that are not visible based on the visible fields.
 * @param {object[]} filterComponents - All filter components.
 * @param {object[]} visibleFields - Visible filter components id's.
 * @returns {object[]} Filter components that are not visible.
 */
export const getNotVisibleFields = (filterComponents: IFilter[], visibleFields: string[]) => {
    return filterComponents.filter((filter) => !visibleFields.includes(filter.id));
};

/**
 * Function to get the visible fields based on their IDs from a list of filters.
 * @param {string[]} visibleFilterIds - The IDs of the visible filters.
 * @param {object[]} visibleFields - The list of visible filter configurations.
 * @returns {object[]} Visible filter fields.
 */
export const getVisibleFields = (visibleFilterIds: string[], visibleFields: IFilter[]) => {
    const parsedFields = fieldsById(visibleFields);
    return visibleFilterIds.map((id) => parsedFields[id]).filter((filter) => typeof filter !== "undefined");
};

/**
 * Function to create a dictionary of filters indexed by their IDs.
 * @param {object[]} filters - The list of filter configurations.
 * @returns {object} Dictionary of filters indexed by their IDs.
 */
export const fieldsById = (filters: IFilter[]): Record<string, IFilter> => {
    return filters.reduce((base, filter) => ({ ...base, [filter.id]: filter }), {});
};

/**
 * Function to get a filter by its ID from a list of filters.
 * @param {object[]} filters - The list of filters.
 * @param {string} id - The ID of the filter to find.
 * @returns {object | undefined} The filter with the specified ID, or undefined if not found.
 */
export const getFieldById = (filters: IFilter[], id: string) => {
    return filters.find((filter) => filter.id === id);
};
